/**
 * Public "system of proof" self-assessment — the Evidence Graph Maturity Model
 * (EGMM), faithful to the Ekselens canon: 25 statements as level-rungs, each
 * scored 0/1/2, summing to 0–10 per pillar; aggregate = mean.
 *   Route: /system-of-proof
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, ShieldCheck, AlertTriangle } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import Layout from "../components/Layout";
import { EvidenceTicker } from "../components/EvidenceTicker";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SCORE_LABEL = { 0: "Not observed", 1: "Partially", 2: "Fully observed" };

function ScorePicker({ value, onChange, idBase }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {[0, 1, 2].map((sc) => (
        <button key={sc} type="button" data-testid={`${idBase}-sc-${sc}`} onClick={() => onChange(sc)} title={SCORE_LABEL[sc]}
          className={`px-2.5 py-1.5 rounded text-[12px] font-semibold border transition-colors ${value === sc
            ? "bg-[#7737F6] text-white border-[#7737F6]" : "border-zinc-300 text-zinc-500 hover:bg-zinc-50"}`}>
          {sc}<span className="hidden sm:inline"> · {SCORE_LABEL[sc]}</span>
        </button>
      ))}
    </div>
  );
}

export default function SystemOfProof() {
  const [model, setModel] = useState(null);
  const [responses, setResponses] = useState({});
  const [phase, setPhase] = useState("intro");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState({ full_name: "", email: "", organisation: "", role: "", consent: false });

  useEffect(() => {
    axios.get(`${API}/public/evidence-maturity/model`).then((r) => setModel(r.data))
      .catch(() => toast.error("Could not load the assessment"));
  }, []);

  const total = useMemo(() => model ? model.pillars.reduce((n, p) => n + p.statements.length, 0) : 0, [model]);
  const answered = Object.keys(responses).length;
  const setResp = (id, sc) => setResponses((r) => ({ ...r, [id]: sc }));

  const showResult = async () => {
    try {
      const { data } = await axios.post(`${API}/public/evidence-maturity/score`, { responses });
      setResult(data); setPhase("result"); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Could not score the assessment"); }
  };
  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consent) { toast.error("Please tick consent to receive your result"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/evidence-maturity/lead`, { ...lead, responses });
      setPhase("submitted"); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  if (!model) return <Layout><div className="max-w-3xl mx-auto px-4 py-32 text-center text-zinc-500">Loading…</div></Layout>;

  const bench = result?.sector_benchmarks?.["UK bank"] || {};
  const radarData = (result?.pillars || []).map((p) => ({ pillar: p.name, You: p.total ?? 0, "UK bank": bench[p.key] ?? 0 }));

  return (
    <Layout>
      <section className="relative bg-[#0B0B0C] text-white py-20 md:py-28 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <span className="inline-flex items-center px-3 py-1 text-[10.5px] font-bold tracking-[0.18em] text-white bg-[#7737F6] uppercase" data-testid="sop-badge">System of proof</span>
            <span className="text-[11px] font-semibold tracking-[0.22em] text-zinc-400 uppercase">EKSELENS · Reinvention assurance</span>
          </div>
          <h1 className="ek-heading text-4xl sm:text-5xl lg:text-6xl mb-6" data-testid="sop-title">Evidence Graph Maturity</h1>
          <p className="text-[#A179FA] text-base md:text-lg font-light italic mb-6">How defensible is your enterprise's system of proof?</p>
          <p className="text-zinc-300 text-base md:text-lg max-w-3xl font-light" data-testid="sop-intro">
            Transformations rarely fail on design — they fail on trust. Rate your organisation across the five pillars of a
            defensible evidence trail: Provenance, Challenge, Continuity, Control and Disclosure. 25 statements scored against
            evidence, not intention. ~5 minutes, an honest maturity profile. No login required.
          </p>
          <EvidenceTicker />
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">

          {phase === "intro" && (
            <div className="text-center" data-testid="sop-intro-cta">
              <h2 className="ek-heading text-2xl md:text-3xl mb-4">Ready to start?</h2>
              <p className="text-zinc-600 max-w-2xl mx-auto mb-4 text-[15px]">
                Each pillar carries five statements — one per maturity level. Score each: <strong>0 not observed · 1 partially observed · 2 fully observed & evidenced</strong>.
                Pillars sum to 0–10; your aggregate maps to a band (Absent → Continuous).
              </p>
              <p className="text-zinc-500 max-w-2xl mx-auto mb-8 text-[13px] italic">Score against what you can show, not what you intend. If you cannot demonstrate it, it is not there.</p>
              <button type="button" onClick={() => setPhase("questions")} className="ek-btn ek-btn-primary text-[14px]" data-testid="sop-start-btn">
                Start the self-assessment <ArrowRight size={14} />
              </button>
              <p className="text-zinc-500 mt-6 text-[12.5px]">No login. Email only needed to receive your branded PDF.</p>
            </div>
          )}

          {phase === "questions" && (
            <div data-testid="sop-questions">
              <div className="flex items-center justify-between mb-6 sticky top-0 bg-white py-3 z-10 border-b border-zinc-100">
                <span className="text-[13px] text-zinc-500">{answered}/{total} scored</span>
                <button type="button" onClick={showResult} disabled={answered === 0}
                  className="ek-btn ek-btn-primary text-[13px] disabled:opacity-40" data-testid="sop-see-result">
                  See my result <ArrowRight size={14} />
                </button>
              </div>
              <div className="space-y-8">
                {model.pillars.map((pillar) => (
                  <div key={pillar.key} data-testid={`sop-pillar-${pillar.key}`}>
                    <h3 className="ek-heading text-xl mb-1">{pillar.name}</h3>
                    <p className="text-zinc-500 text-[13px] mb-4">{pillar.definition} — <span className="italic">{pillar.question}</span></p>
                    <div className="space-y-4">
                      {pillar.statements.map((st) => (
                        <div key={st.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border-b border-zinc-50 pb-3">
                          <span className="text-[13.5px] text-zinc-700 md:flex-1 md:pr-6"><span className="text-zinc-400 mr-1.5 text-[12px]">L{st.level}</span>{st.text}</span>
                          <ScorePicker value={responses[st.id]} onChange={(sc) => setResp(st.id, sc)} idBase={`sop-${st.id}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <button type="button" onClick={showResult} disabled={answered === 0}
                  className="ek-btn ek-btn-primary text-[14px] disabled:opacity-40">See my result <ArrowRight size={14} /></button>
              </div>
            </div>
          )}

          {phase === "result" && result && (
            <div data-testid="sop-result">
              <div className="grid lg:grid-cols-2 gap-8 items-center mb-10">
                <div className="border border-zinc-200 rounded-lg p-3">
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={radarData} outerRadius="70%">
                      <PolarGrid />
                      <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 12 }} />
                      <PolarRadiusAxis domain={[0, 10]} tickCount={6} tick={{ fontSize: 9 }} />
                      <Radar name="You" dataKey="You" stroke="#7737F6" fill="#7737F6" fillOpacity={0.35} />
                      <Radar name="UK bank" dataKey="UK bank" stroke="#a1a1aa" fill="#a1a1aa" fillOpacity={0} strokeDasharray="4 3" />
                    </RadarChart>
                  </ResponsiveContainer>
                  <p className="text-[11px] text-zinc-400 text-center">Dashed line: indicative UK-bank benchmark (illustrative)</p>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-400 mb-1">Aggregate maturity</div>
                  <div className="text-5xl font-bold text-[#7737F6] mb-1" data-testid="sop-overall">{result.aggregate ?? "—"}<span className="text-2xl text-zinc-400">/10</span></div>
                  <div className="text-[13px] font-semibold text-zinc-600 mb-3">{result.aggregate_band || "—"}</div>
                  <p className="text-zinc-700 text-[14px] leading-relaxed mb-3" data-testid="sop-verdict">{result.verdict}</p>
                  <p className="text-[13px] text-zinc-500"><b>Profile:</b> {result.profile_pattern}</p>
                </div>
              </div>

              {result.priorities?.length > 0 && (
                <div className="mb-8">
                  <h3 className="ek-heading text-xl mb-3">Where we'd start</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {result.priorities.map((p) => (
                      <div key={p.key} className="border border-zinc-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-1"><ShieldCheck size={15} className="text-[#7737F6]" /><b className="text-[14px]">{p.name}</b><span className="text-[12px] text-zinc-400">· {p.total}/10</span></div>
                        <p className="text-[13px] text-zinc-600">{p.target}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.erio_readiness && (
                <div className="mb-10 bg-zinc-50 border border-zinc-200 rounded-lg p-4" data-testid="sop-erio">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-400 mb-1">ERIO readiness</div>
                  <p className="text-[14px] text-zinc-700">{result.erio_readiness.recommendation}</p>
                  {result.erio_readiness.blocked && <p className="flex items-center gap-1.5 text-[13px] text-amber-700 mt-1"><AlertTriangle size={14} /> {result.erio_readiness.blocked}</p>}
                </div>
              )}

              <div className="bg-[#0B0B0C] text-white rounded-lg p-6 md:p-8">
                <h3 className="ek-heading text-xl md:text-2xl mb-2">Get your branded result — and the step beyond a self-assessment</h3>
                <p className="text-zinc-300 text-[14px] mb-6 max-w-2xl">
                  Inside an Ekselens engagement, each pillar is <em>auto-evidenced</em> from your live programme — evidence chain,
                  four-eyes sign-off, retention, export gate and regulator packs — so you can <strong>show, not assert</strong>, where you stand.
                </p>
                <form onSubmit={submitLead} className="grid sm:grid-cols-2 gap-3" data-testid="sop-lead-form">
                  <input required placeholder="Full name" value={lead.full_name} data-testid="sop-lead-name"
                    onChange={(e) => setLead({ ...lead, full_name: e.target.value })} className="bg-white/10 border border-white/20 rounded px-3 py-2 text-[14px] placeholder-zinc-400" />
                  <input required type="email" placeholder="Work email" value={lead.email} data-testid="sop-lead-email"
                    onChange={(e) => setLead({ ...lead, email: e.target.value })} className="bg-white/10 border border-white/20 rounded px-3 py-2 text-[14px] placeholder-zinc-400" />
                  <input placeholder="Organisation" value={lead.organisation} data-testid="sop-lead-org"
                    onChange={(e) => setLead({ ...lead, organisation: e.target.value })} className="bg-white/10 border border-white/20 rounded px-3 py-2 text-[14px] placeholder-zinc-400" />
                  <input placeholder="Role" value={lead.role} data-testid="sop-lead-role"
                    onChange={(e) => setLead({ ...lead, role: e.target.value })} className="bg-white/10 border border-white/20 rounded px-3 py-2 text-[14px] placeholder-zinc-400" />
                  <label className="sm:col-span-2 flex items-start gap-2 text-[12.5px] text-zinc-300">
                    <input type="checkbox" checked={lead.consent} data-testid="sop-lead-consent"
                      onChange={(e) => setLead({ ...lead, consent: e.target.checked })} className="mt-0.5" />
                    I agree to Ekselens emailing me my result and a relevant follow-up. See our <Link to="/privacy" className="text-[#A179FA] underline">privacy policy</Link>.
                  </label>
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={submitting} className="ek-btn ek-btn-primary text-[14px] disabled:opacity-40" data-testid="sop-lead-submit">
                      {submitting ? "Sending…" : "Email me my result"} <ArrowRight size={14} />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {phase === "submitted" && (
            <div className="text-center max-w-2xl mx-auto" data-testid="sop-submitted">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#7737F6] text-white rounded-full mb-6"><CheckCircle2 size={28} /></div>
              <h2 className="ek-heading text-2xl md:text-3xl mb-3">Your result is on its way.</h2>
              <p className="text-zinc-600 text-[14.5px]">Check your inbox for your branded Evidence Graph Maturity result. A founder-led conversation will follow with a sector-calibrated next step. Meanwhile, explore our <Link to="/enterprise360" className="text-[#7737F6] hover:underline">Enterprise360 reinvention engine</Link>.</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
